"use client";

/**
 * AskHoopsButton — animated button adapted from AnimatedGenerateButton.
 * Source copied verbatim from 21st.dev/r/moazamtrade/animated-generate-button-shadcn-tailwind
 * and renamed. SVG sparkle icon replaced with orb-icon.lottie placeholder.
 */

import { cn } from "@/lib/utils";
import { DotLottiePlayer } from "@dotlottie/react-player";
import "@dotlottie/react-player/dist/index.css";

export interface AskHoopsButtonProps {
  className?: string;
  labelIdle?: string;
  labelActive?: string;
  generating?: boolean;
  highlightHueDeg?: number;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
}

export default function AskHoopsButton({
  className,
  labelIdle = "Ask Hoops",
  labelActive = "Thinking...",
  generating = false,
  highlightHueDeg = 82, // lime-gold hue matching --accent
  onClick,
  type = "button",
  disabled = false,
  id,
  ariaLabel,
}: AskHoopsButtonProps) {
  return (
    <div className={cn("relative inline-block", className)} id={id}>
      <button
        type={type}
        aria-label={ariaLabel || (generating ? labelActive : labelIdle)}
        aria-pressed={generating}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          "ui-anim-btn",
          "relative flex items-center justify-center cursor-pointer select-none",
          "rounded-[24px] px-4 py-2",
          "text-[hsl(var(--foreground))]",
          "transition-[box-shadow,border,background-color] duration-400",
        )}
        style={
          {
            ["--highlight-hue" as string]: `${highlightHueDeg}deg`,
            background: "var(--surface)",
            borderColor: "var(--border-subtle)",
          } as React.CSSProperties
        }
      >
        {/* Orb icon — larger, plays on hover */}
        <div className="ui-anim-btn-orb mr-2.5 flex-grow-0 flex-shrink-0 w-8 h-8 rounded-full overflow-hidden">
          <DotLottiePlayer
            src="/orb-icon.lottie"
            autoplay
            loop
            style={{ width: 32, height: 32 }}
          />
        </div>
        <div className="ui-anim-txt-wrapper relative flex items-center whitespace-nowrap" style={{ minWidth: "6.5em" }}>
          <div
            className={cn(
              "ui-anim-txt-1 absolute",
              generating ? "opacity-0" : "animate-[ui-appear_1s_ease-in-out_forwards]",
            )}
          >
            {Array.from(labelIdle).map((ch, i) => (
              <span key={i} className="ui-anim-letter inline-block">
                {ch === " " ? "\u00A0" : ch}
              </span>
            ))}
          </div>
          <div
            className={cn(
              "ui-anim-txt-2 absolute",
              generating ? "opacity-100" : "opacity-0",
            )}
          >
            {Array.from(labelActive).map((ch, i) => (
              <span key={i} className="ui-anim-letter inline-block">
                {ch === " " ? "\u00A0" : ch}
              </span>
            ))}
          </div>
        </div>
      </button>
      <style>{`
        .ui-anim-btn {
          --padding: 4px;
          --radius: 24px;
          --transition: 0.4s;
          --highlight: hsl(var(--highlight-hue), 100%, 70%);
          --highlight-50: hsla(var(--highlight-hue), 100%, 70%, 0.5);
          --highlight-30: hsla(var(--highlight-hue), 100%, 70%, 0.3);
          --highlight-20: hsla(var(--highlight-hue), 100%, 70%, 0.2);
          --highlight-80: hsla(var(--highlight-hue), 100%, 70%, 0.8);
          border: 1px solid #2a2a2a;
          border-top-color: #3a3a3a;
          border-bottom-color: #1a1a1a;
          box-shadow:
            inset 0 1px 0 0 #ffffff0d,
            inset 0 -1px 0 0 #0003,
            0 1px 2px #0004,
            0 4px 8px -2px #0006;
          background: linear-gradient(180deg, #1a1a1a 0%, #111 100%);
        }
        /* Shimmer sweep — moves across on idle, subtle */
        @keyframes ui-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .ui-anim-btn::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          pointer-events: none;
          z-index: 1;
          background: linear-gradient(
            105deg,
            transparent 20%,
            rgba(255,255,255,0.03) 40%,
            rgba(255,255,255,0.07) 50%,
            rgba(255,255,255,0.03) 60%,
            transparent 80%
          );
          background-size: 200% 100%;
          animation: ui-shimmer 4s ease-in-out infinite;
          transition: opacity var(--transition);
        }
        .ui-anim-btn::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          pointer-events: none;
          background-image: linear-gradient(0deg, #fff, var(--highlight), var(--highlight-50), 8%, transparent);
          opacity: 0;
          transition: opacity var(--transition), filter var(--transition);
        }
        .ui-anim-letter {
          color: #ffffffaa;
          animation: none;
          transition: color var(--transition), text-shadow var(--transition);
        }
        @keyframes ui-letter-anim { 50% { text-shadow: 0 0 3px #fff8; color: #fff; } }
        .ui-anim-btn:hover .ui-anim-letter { animation: ui-letter-anim 2s ease-in-out infinite; }
        .ui-anim-btn-orb {
          opacity: 0.8;
          transition: opacity var(--transition), filter var(--transition);
        }
        .ui-anim-btn:hover .ui-anim-btn-orb {
          opacity: 1;
          filter: drop-shadow(0 0 3px var(--highlight)) drop-shadow(0 -4px 6px #0009);
        }
        @keyframes ui-appear { 0% { opacity: 0; } 100% { opacity: 1; } }

        /* Hover — lifted, brighter top edge, glow */
        .ui-anim-btn:hover {
          border-color: hsla(var(--highlight-hue), 80%, 60%, 0.35);
          border-top-color: hsla(var(--highlight-hue), 80%, 70%, 0.4);
          border-bottom-color: #1a1a1a;
          box-shadow:
            inset 0 1px 0 0 rgba(255,255,255,0.12),
            inset 0 -1px 0 0 #0004,
            0 2px 4px #0005,
            0 8px 16px -4px #0008,
            0 0 20px -4px var(--highlight-20);
          background: linear-gradient(180deg, #1e1e1e 0%, #131313 100%);
        }
        .ui-anim-btn:hover::before {
          animation: ui-shimmer 2s ease-in-out infinite;
          background: linear-gradient(
            105deg,
            transparent 15%,
            rgba(255,255,255,0.04) 35%,
            rgba(255,255,255,0.1) 50%,
            rgba(255,255,255,0.04) 65%,
            transparent 85%
          );
          background-size: 200% 100%;
        }
        .ui-anim-btn:hover::after {
          opacity: 0.6;
          mask-image: linear-gradient(0deg, #fff, transparent);
          -webkit-mask-image: linear-gradient(0deg, #fff, transparent);
        }

        /* Active — pressed in, inverted shadow */
        .ui-anim-btn:active {
          border-color: hsla(var(--highlight-hue), 80%, 60%, 0.5);
          border-top-color: #1a1a1a;
          border-bottom-color: hsla(var(--highlight-hue), 80%, 70%, 0.3);
          box-shadow:
            inset 0 2px 4px #0006,
            inset 0 1px 1px #0003,
            0 1px 0px #fff1;
          background: linear-gradient(180deg, #0f0f0f 0%, #161616 100%);
          transform: translateY(1px);
        }
        .ui-anim-btn:active::before { opacity: 0; }
        .ui-anim-btn:active::after {
          opacity: 1;
          mask-image: linear-gradient(0deg, #fff, transparent);
          -webkit-mask-image: linear-gradient(0deg, #fff, transparent);
          filter: brightness(150%);
        }
        .ui-anim-btn:active .ui-anim-letter { text-shadow: 0 0 1px hsla(var(--highlight-hue), 100%, 90%, 0.9); animation: none; }

        /* Letter stagger delays */
        ${Array.from({ length: 13 }, (_, i) => `.ui-anim-txt-1 .ui-anim-letter:nth-child(${i + 1}),.ui-anim-txt-2 .ui-anim-letter:nth-child(${i + 1}){animation-delay:${(i * 0.08).toFixed(2)}s}`).join("\n")}

        .ui-anim-btn:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
